import { type ScriptableTooltipContext, type TooltipModel } from "chart.js";
import { groupBy } from "lodash";

import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { MetricFormatters } from "@north-beam/nb-client-side";

export function chartTooltip(context: ScriptableTooltipContext<"line">) {
  const chart = context.chart;
  const tooltip = context.tooltip;
  const options = context.tooltip.options;

  let tooltipEl = chart.canvas?.parentNode?.querySelector("div");

  tooltip.xAlign = "right";
  const datapointDates = new Set(tooltip.dataPoints.map((dp) => dp.label));
  if (datapointDates.size > 1) {
    let maxXPos = -Infinity;
    let maxDate = "";
    tooltip.dataPoints.forEach((datapoint) => {
      if (datapoint.element.x > maxXPos) {
        maxXPos = datapoint.element.x;
        maxDate = datapoint.label;
      }
    });
    tooltip.dataPoints = tooltip.dataPoints.filter(
      (datapoint) => datapoint.label === maxDate,
    );
  }

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    chart.canvas.parentNode?.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (tooltip.body && tooltip.dataPoints.length) {
    const content = buildTooltipContent(tooltip, options);
    const date = tooltip.dataPoints[0].label;

    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[0px]",
      center: "translate-x-[0px]",
      right: "translate-x-[0px]",
    }[tooltip.xAlign];

    tooltipEl.innerHTML = `
      <div class="relative" style="transform: none">
        <div
          data-tooltip-id="content"
          style="min-width: 330px;"
          class="
            bg-v2-primary border border-v2-secondary py-5 px-4 rounded-md space-y-2.5
            shadow whitespace-nowrap ${contentPosition}
          "
        >
          <div class="text-lg font-bold leading-0 py-2">${date}</div>
          ${content}
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-v2-primary absolute top-[0px] border-t border-l border-v2-secondary
            left-[0px] rotate-[0deg]
          "
        >
        </div>
      </div>
    `;
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: "translate(10px, 10px",
    left: positionX + tooltip.caretX + "px",
    top: positionY + tooltip.caretY + "px",
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();

    if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove("left-[10px]");
      arrowEl.classList.add("right-[10px]");
      contentEl.style.transform = "translateX(-100%)";
      tooltipEl.style.transform = "translate(-100%, 10px)";
    }

    if (contentRect.bottom > window.innerHeight) {
      arrowEl.classList.remove("top-[-6px]", "rotate-[45deg]");
      arrowEl.classList.add("bottom-[-6px]", "rotate-[225deg]");
      tooltipEl.style.transform = "translate(10px, -100%)";
    }
  }
}

const buildTooltipContent = (
  tooltip: TooltipModel<"line" | "bar">,
  options: any,
) => {
  const content: string[] = [];

  const series = groupBy(tooltip.dataPoints, (e) => (e.dataset as any).series);

  Object.entries(series).forEach(([key, seriesItem]) => {
    let result = `
      <div class="mt-3">
        <div class="font-bold mb-2">${key}</div>
    `;

    seriesItem.forEach(({ dataset, parsed }) => {
      let value = options.isRevenue
        ? MetricFormatters.currency(
            parsed.y,
            options.user.currencies.currentCurrency,
            { precision: "exact" },
          )
        : MetricFormatters.decimal(
            parsed.y,
            options.user.currencies.currentCurrency,
            { precision: "exact" },
          );

      if (dataset.label === "Spend") {
        value = MetricFormatters.currency(
          parsed.y,
          options.user.currencies.currentCurrency,
          { precision: "exact" },
        );
      }

      const legend = buildLegend(
        {
          borderColor:
            dataset.label === "Low"
              ? dataset.backgroundColor
              : dataset.borderColor,
          backgroundColor: dataset.backgroundColor,
          borderDash: dataset.borderDash ?? [],
          borderWidth: dataset.label === "Low" ? 0 : 4,
        } as any,
        dataset.label === "Low",
      );

      result += `
        <div class="flex items-center justify-between space-y-1">
          <div class="flex items-center gap-2">
            <div style="width: 22px;">${
              dataset.label !== "High" ? legend : ""
            }</div>
            <div class="truncate mr-4" style="max-width:240px;">${
              dataset.label === "Low"
                ? "Forecast Range (low → high)"
                : dataset.label === "High"
                ? ""
                : dataset.label
            }</div>
          </div>
          <div class="font-mono">${value}</div>
        </div>
      `;
    });

    result += "</div>";

    content.push(result);
  });

  const divider = `<div class="${"border-t border-v2-secondary h-1 mt-3"}"></div>`;

  return `
    <div class="text-xs">
      ${divider}
      ${content.join(divider)}
    </div>
  `;
};
